import { showToast } from '@components/utils/helper';
import IdentityVerificationCard from '@components/utils/identity-verification';
import PayoutDetails from '@components/utils/payout-details';
import { useIsMobile } from '@hooks/useIsMobile';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';

import { TypeButton } from '../../../utils/button';
import { TypeCheckbox } from '../../../utils/checkbox';
import { TypeInput } from '../../../utils/input';
import { TypeRadioGroup } from '../../../utils/radio-group';
import DuplicateApplicationModal from './DuplicateApplicationModal';
import { useDuplicateCheck } from './duplicateCheck';
import { type BasicInfoProps, fieldsForRole, GENDER_OPTIONS, MAX_DOB_DATE } from './helper';
import {
	BasicInfoLayout,
	ChevronIcon,
	CTASection,
	DuplicateStatusChip,
	FieldsColumn,
	FieldsSection,
	FieldsSectionEyebrow,
	FieldsSectionGrid,
	MessagingCheckboxContainer,
	MessagingCheckboxLabel,
	PhoneFieldWrapper,
	SectionHeader,
	SocialIconWrapper,
	SocialMediaGrid,
	StickyVerificationColumn,
	VerifiedBadgeInline,
	VerifiedFieldRow,
} from './styled';

// ---------- Brand icons ----------

const FacebookIcon = () => (
	<SocialIconWrapper aria-hidden='true'>
		<svg
			viewBox='0 0 24 24'
			fill='#1877F2'
			width='16'
			height='16'
		>
			<path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
		</svg>
	</SocialIconWrapper>
);

const XIcon = () => (
	<SocialIconWrapper aria-hidden='true'>
		<svg
			viewBox='0 0 24 24'
			fill='currentColor'
			width='16'
			height='16'
		>
			<path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
		</svg>
	</SocialIconWrapper>
);

const InstagramIcon = () => (
	<SocialIconWrapper aria-hidden='true'>
		<svg
			viewBox='0 0 24 24'
			width='16'
			height='16'
		>
			<defs>
				<linearGradient
					id='instagram-gradient'
					x1='0%'
					y1='100%'
					x2='100%'
					y2='0%'
				>
					<stop
						offset='0%'
						stopColor='#FFDC80'
					/>
					<stop
						offset='25%'
						stopColor='#F77737'
					/>
					<stop
						offset='50%'
						stopColor='#E1306C'
					/>
					<stop
						offset='75%'
						stopColor='#C13584'
					/>
					<stop
						offset='100%'
						stopColor='#833AB4'
					/>
				</linearGradient>
			</defs>
			<path
				fill='url(#instagram-gradient)'
				d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z'
			/>
		</svg>
	</SocialIconWrapper>
);

const WhatsAppIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='#25D366'
		width='16'
		height='16'
		aria-hidden='true'
	>
		<path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z' />
	</svg>
);

const TelegramIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='#0088CC'
		width='16'
		height='16'
		aria-hidden='true'
	>
		<path d='M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z' />
	</svg>
);

const ChevronDownIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='currentColor'
		width='16'
		height='16'
		aria-hidden='true'
	>
		<path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z' />
	</svg>
);

// ---------- Component ----------

const BasicInfo = ({ hookForm, onNext, actions, states, electionId, role = 'agent' }: BasicInfoProps) => {
	const {
		register,
		control,
		formState: { errors, isValid, dirtyFields },
		watch,
		getValues,
		setFocus,
	} = hookForm;

	const requireVoterCard = fieldsForRole(role).requiresVoterCard;

	const { isMobile } = useIsMobile();
	const [socialExpanded, setSocialExpanded] = useState(!isMobile);

	const dup = useDuplicateCheck({ electionId });
	const [dupModalOpen, setDupModalOpen] = useState(false);

	const isFieldValid = (fieldName: keyof typeof dirtyFields) => {
		const isDirty = dirtyFields[fieldName];
		const hasError = !!errors[fieldName];
		return !!isDirty && !hasError;
	};

	const kycEnrolBusy = states?.agent?.kycEnrolFace_Loading;
	const kycCheckBusy = states?.agent?.kycCheckId_Loading;
	const isChecking = dup.status === 'checking';
	const isLoading = !!(kycEnrolBusy || kycCheckBusy || isChecking);

	const faceId = watch('faceId') as string | null | undefined;
	const cardImage = watch('votersCardImage') as string | null | undefined;
	const reviewRequired = !!watch('kycReviewRequired');

	const selfieBadgeTone: 'success' | 'warning' | 'idle' = faceId ? (reviewRequired ? 'warning' : 'success') : 'idle';

	const cardBadgeTone: 'success' | 'warning' | 'idle' = cardImage ? (reviewRequired ? 'warning' : 'success') : 'idle';

	// Live, debounced duplicate check once email + phone + VIN are each individually valid.
	const watchedEmail = watch('email');
	const watchedPhone = watch('phoneNumber');
	const watchedVin = watch('votersCardRegistrationNumber');

	useEffect(() => {
		if (!electionId) return;
		const emailOk = isFieldValid('email') && !!watchedEmail;
		const phoneOk = isFieldValid('phoneNumber') && !!watchedPhone;
		const vinOk = isFieldValid('votersCardRegistrationNumber') && !!watchedVin;

		if (emailOk && phoneOk && vinOk) {
			dup.scheduleLiveCheck({
				email: watchedEmail,
				phoneNumber: watchedPhone,
				votersCardRegistrationNumber: watchedVin,
			});
		} else {
			dup.reset();
		}
	}, [watchedEmail, watchedPhone, watchedVin, electionId]);

	const focusEmail = () => {
		// Defer past the modal's close transition — react-bootstrap restores focus to the trigger
		// on exit, which would otherwise clobber this focus call.
		setTimeout(() => {
			try {
				setFocus('email');
			} catch {
				// setFocus can throw if the field isn't mounted; safe to ignore.
			}
		}, 200);
	};

	const handleNext = async () => {
		if (!electionId) {
			showToast('error', 'Election details are still loading. Please try again in a moment.');
			return;
		}

		const result = await dup.runCheck({
			email: getValues('email'),
			phoneNumber: getValues('phoneNumber') ?? '',
			votersCardRegistrationNumber: getValues('votersCardRegistrationNumber') ?? '',
		});

		if (result.status === 'passed') {
			onNext();
			return;
		}

		if (result.status === 'duplicate') {
			setDupModalOpen(true);
			return;
		}

		showToast('error', 'We couldn’t verify your details right now. Please check your connection and try again.');
	};

	return (
		<>
			<BasicInfoLayout>
				<StickyVerificationColumn>
					<IdentityVerificationCard
						hookForm={hookForm}
						actions={actions}
						showVoterCard={requireVoterCard}
					/>
				</StickyVerificationColumn>

				<FieldsColumn>
					<FieldsSection>
						<FieldsSectionEyebrow>Personal</FieldsSectionEyebrow>
						<FieldsSectionGrid>
							<TypeInput
								label='First Name'
								placeholder='Enter First Name'
								type='text'
								customwidth='100%'
								error={errors.firstName?.message}
								isvalid={isFieldValid('firstName')}
								{...register('firstName')}
							/>

							<TypeInput
								label='Last Name'
								placeholder='Enter Last Name'
								type='text'
								customwidth='100%'
								error={errors.lastName?.message}
								isvalid={isFieldValid('lastName')}
								{...register('lastName')}
							/>

							<TypeInput
								label='Date of Birth'
								type='date'
								customwidth='100%'
								max={MAX_DOB_DATE}
								error={errors.dateOfBirth?.message}
								isvalid={isFieldValid('dateOfBirth')}
								{...register('dateOfBirth')}
							/>

							<Controller
								name='gender'
								control={control}
								render={({ field: { onChange, value, ref } }) => (
									<TypeRadioGroup
										label='Gender'
										options={GENDER_OPTIONS}
										error={errors.gender?.message}
										isvalid={isFieldValid('gender')}
										value={value}
										onChange={onChange}
										ref={ref}
									/>
								)}
							/>
						</FieldsSectionGrid>
					</FieldsSection>

					<FieldsSection>
						<FieldsSectionEyebrow>Contact</FieldsSectionEyebrow>
						<FieldsSectionGrid>
							<TypeInput
								label='Email'
								placeholder='email@domain.com'
								type='email'
								customwidth='100%'
								error={errors.email?.message}
								isvalid={isFieldValid('email')}
								{...register('email')}
							/>

							<PhoneFieldWrapper>
								<TypeInput
									label='Phone Number'
									placeholder='080XXXXXXXX'
									type='tel'
									customwidth='100%'
									error={errors.phoneNumber?.message}
									isvalid={isFieldValid('phoneNumber')}
									{...register('phoneNumber')}
								/>
								<MessagingCheckboxContainer>
									<span>Connected to:</span>
									<MessagingCheckboxLabel>
										<WhatsAppIcon />
										<TypeCheckbox
											label='WhatsApp'
											{...register('isWhatsAppConnected')}
										/>
									</MessagingCheckboxLabel>
									<MessagingCheckboxLabel>
										<TelegramIcon />
										<TypeCheckbox
											label='Telegram'
											{...register('isTelegramConnected')}
										/>
									</MessagingCheckboxLabel>
								</MessagingCheckboxContainer>
							</PhoneFieldWrapper>
						</FieldsSectionGrid>
					</FieldsSection>

					{requireVoterCard && (
						<FieldsSection>
							<FieldsSectionEyebrow>Voter Identity</FieldsSectionEyebrow>
							<VerifiedFieldRow>
								<TypeInput
									label='Voter Card ID (VIN)'
									placeholder='19-character VIN from your PVC'
									type='text'
									customwidth='100%'
									error={errors.votersCardRegistrationNumber?.message}
									isvalid={isFieldValid('votersCardRegistrationNumber')}
									{...register('votersCardRegistrationNumber')}
								/>
								<VerifiedBadgeInline tone={cardBadgeTone}>
									{cardBadgeTone === 'success'
										? '✓ Card verified'
										: cardBadgeTone === 'warning'
											? '⚠ Under review'
											: 'Not verified'}
								</VerifiedBadgeInline>
							</VerifiedFieldRow>
						</FieldsSection>
					)}

					<FieldsSection>
						<FieldsSectionEyebrow>Identity status</FieldsSectionEyebrow>
						<VerifiedFieldRow>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '4px',
								}}
							>
								<span
									style={{
										fontFamily: 'var(--font-family-medium)',
										fontSize: '13px',
										color: 'var(--color-text-secondary)',
									}}
								>
									Selfie liveness check
								</span>
								<span
									style={{
										fontFamily: 'var(--font-family-regular)',
										fontSize: '12px',
										color: 'var(--color-text-tertiary)',
									}}
								>
									Capture your selfie in the panel on the left.
								</span>
							</div>
							<VerifiedBadgeInline tone={selfieBadgeTone}>
								{selfieBadgeTone === 'success'
									? '✓ Live human'
									: selfieBadgeTone === 'warning'
										? '⚠ Under review'
										: 'Not verified'}
							</VerifiedBadgeInline>
						</VerifiedFieldRow>
					</FieldsSection>

					<FieldsSection>
						<FieldsSectionEyebrow>Payout details</FieldsSectionEyebrow>
						<FieldsSectionGrid>
							<PayoutDetails hookForm={hookForm} />
						</FieldsSectionGrid>
					</FieldsSection>

					<FieldsSection>
						<SectionHeader
							onClick={isMobile ? () => setSocialExpanded((prev) => !prev) : undefined}
							style={isMobile ? { cursor: 'pointer' } : undefined}
							role={isMobile ? 'button' : undefined}
							tabIndex={isMobile ? 0 : undefined}
						>
							<h4>Social Media</h4>
							<span>(Optional)</span>
							{isMobile && (
								<ChevronIcon isExpanded={socialExpanded}>
									<ChevronDownIcon />
								</ChevronIcon>
							)}
						</SectionHeader>
						{socialExpanded && (
							<SocialMediaGrid>
								<TypeInput
									inputPrefix={
										<>
											<FacebookIcon /> @
										</>
									}
									placeholder='username'
									type='text'
									customwidth='100%'
									{...register('facebookHandle')}
								/>
								<TypeInput
									inputPrefix={
										<>
											<XIcon /> @
										</>
									}
									placeholder='username'
									type='text'
									customwidth='100%'
									{...register('twitterHandle')}
								/>
								<TypeInput
									inputPrefix={
										<>
											<InstagramIcon /> @
										</>
									}
									placeholder='username'
									type='text'
									customwidth='100%'
									{...register('instagramHandle')}
								/>
							</SocialMediaGrid>
						)}
					</FieldsSection>
				</FieldsColumn>
			</BasicInfoLayout>

			<CTASection>
				{dup.status !== 'idle' ? (
					<DuplicateStatusChip
						type='button'
						tone={dup.status === 'passed' ? 'passed' : dup.status === 'duplicate' ? 'duplicate' : 'checking'}
						onClick={dup.status === 'duplicate' ? () => setDupModalOpen(true) : undefined}
						aria-live='polite'
					>
						{dup.status === 'checking' && 'Checking for existing applications…'}
						{dup.status === 'passed' && '✓ No existing application found'}
						{dup.status === 'duplicate' && '⚠ Application already exists — review'}
						{dup.status === 'error' && 'Couldn’t verify — tap Continue to retry'}
					</DuplicateStatusChip>
				) : (
					<div />
				)}
				<TypeButton
					title='Continue →'
					onClick={handleNext}
					disabled={!isValid || isChecking}
					className='nextbtn'
					load={isLoading}
				/>
			</CTASection>

			<DuplicateApplicationModal
				isOpen={dupModalOpen}
				setIsOpen={setDupModalOpen}
				message={dup.message}
				email={getValues('email')}
				phoneNumber={getValues('phoneNumber') ?? ''}
				onEdit={focusEmail}
			/>
		</>
	);
};

export default BasicInfo;
